import { DiscordMarkdown } from "@/components/markdown/DiscordMarkdown";
import VillageModal from "@/components/modals/VillageModal";
import { PushNotificationButton } from "@/components/notifications/PushNotificationButton";
import { convertLetters } from "@/utils/letterConversion";
import { gql, useMutation, useQuery, useSubscription } from "@apollo/client";
import {
    BellIcon,
    CheckIcon,
    EnvelopeIcon,
    EnvelopeOpenIcon,
    MegaphoneIcon,
    TrashIcon,
    UserGroupIcon,
} from "@heroicons/react/24/outline";
import { formatDistanceToNow } from "date-fns";
import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

const NOTIFICATIONS_FULL_QUERY = gql`
    query NotificationsFull(
        $unreadOnly: Boolean
        $notificationType: String
        $limit: Int
        $offset: Int
    ) {
        notifications(
            unreadOnly: $unreadOnly
            notificationType: $notificationType
            limit: $limit
            offset: $offset
        ) {
            id
            notificationType
            title
            message
            isRead
            createdAt
            readAt
            actionUrl
            imageUrl
            sender {
                id
                twitchUsername
            }
            isBroadcast
            isGroupMessage
        }
        notificationCount {
            total
            unread
        }
    }
`;

const MARK_NOTIFICATION_AS_READ = gql`
    mutation MarkNotificationAsRead($notificationId: UUID!) {
        markNotificationAsRead(notificationId: $notificationId) {
            success
        }
    }
`;

const MARK_NOTIFICATION_AS_UNREAD = gql`
    mutation MarkNotificationAsUnread($notificationId: UUID!) {
        markNotificationAsUnread(notificationId: $notificationId) {
            success
        }
    }
`;

const MARK_ALL_NOTIFICATIONS_AS_READ = gql`
    mutation MarkAllNotificationsAsRead {
        markAllNotificationsAsRead {
            success
            count
        }
    }
`;

const DELETE_NOTIFICATION = gql`
    mutation DeleteNotification($notificationId: UUID!) {
        deleteNotification(notificationId: $notificationId) {
            success
        }
    }
`;

const NOTIFICATION_SUBSCRIPTION = gql`
    subscription OnNotificationReceived {
        notificationReceived {
            id
            notificationType
            title
            message
            isRead
            createdAt
            readAt
            actionUrl
            imageUrl
            sender {
                id
                twitchUsername
            }
            isBroadcast
            isGroupMessage
        }
    }
`;

interface Notification {
    id: string;
    notificationType: string;
    title: string;
    message: string;
    isRead: boolean;
    createdAt: string;
    readAt?: string;
    actionUrl?: string;
    imageUrl?: string;
    sender?: {
        id: string;
        twitchUsername: string;
    };
    isBroadcast: boolean;
    isGroupMessage: boolean;
}

export default function VillageInboxPage() {
    const [searchParams] = useSearchParams();
    const notificationId = searchParams.get("notification");
    const notificationRefs = useRef<{ [key: string]: HTMLElement | null }>({});
    // If navigating to a specific notification, show all notifications so it's visible
    const [filter, setFilter] = useState<"all" | "unread">(
        notificationId ? "all" : "unread",
    );
    const [selectedNotification, setSelectedNotification] =
        useState<Notification | null>(null);
    const [offset, setOffset] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const limit = 50;

    // Modal state
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [deleteNotificationId, setDeleteNotificationId] = useState<
        string | null
    >(null);

    const { data, loading, refetch, client } = useQuery(
        NOTIFICATIONS_FULL_QUERY,
        {
            variables: {
                unreadOnly: filter === "unread",
                limit,
                offset,
            },
            onCompleted: (data) => {
                // Check if we have more notifications to load
                if (data?.notifications) {
                    setHasMore(data.notifications.length === limit);
                }
            },
        },
    );

    // Subscribe to new notifications
    useSubscription(NOTIFICATION_SUBSCRIPTION, {
        onData: ({ data: subscriptionData }) => {
            console.debug(
                "[VillageInboxPage] Subscription data received:",
                subscriptionData,
            );
            if (subscriptionData?.data?.notificationReceived) {
                const newNotification =
                    subscriptionData.data.notificationReceived;
                console.debug(
                    "[VillageInboxPage] New notification:",
                    newNotification,
                );

                // Add __typename for Apollo cache and ensure all required fields exist
                const notificationWithTypename = {
                    __typename: "NotificationType",
                    imageUrl: null,
                    relatedObjectId: null,
                    relatedObjectType: null,
                    ...newNotification,
                };

                // Directly update the query cache
                try {
                    const existingData = client.cache.readQuery<{
                        notifications: Notification[];
                        notificationCount: { total: number; unread: number };
                    }>({
                        query: NOTIFICATIONS_FULL_QUERY,
                        variables: {
                            unreadOnly: filter === "unread",
                            limit,
                            offset,
                        },
                    });

                    if (existingData) {
                        // Check if notification already exists
                        const exists = existingData.notifications.some(
                            (n: Notification) => n.id === newNotification.id,
                        );

                        if (!exists) {
                            client.cache.writeQuery({
                                query: NOTIFICATIONS_FULL_QUERY,
                                variables: {
                                    unreadOnly: filter === "unread",
                                    limit,
                                    offset,
                                },
                                data: {
                                    notifications: [
                                        notificationWithTypename,
                                        ...existingData.notifications,
                                    ],
                                    notificationCount: {
                                        __typename: "NotificationCountType",
                                        total:
                                            existingData.notificationCount
                                                .total + 1,
                                        unread:
                                            existingData.notificationCount
                                                .unread + 1,
                                    },
                                },
                            });
                            console.debug(
                                "[VillageInboxPage] Added notification to cache",
                            );
                        } else {
                            console.debug(
                                "[VillageInboxPage] Notification already in cache",
                            );
                        }
                    }
                } catch (error) {
                    console.error(
                        "[VillageInboxPage] Cache update error:",
                        error,
                    );
                    // Fallback to refetch if cache update fails
                    refetch();
                }
            }
        },
        onError: (error) => {
            console.error("[VillageInboxPage] Subscription error:", error);
        },
    });

    const [markAsRead] = useMutation(MARK_NOTIFICATION_AS_READ, {
        onCompleted: () => {
            refetch();
            // Update the selected notification's read status immediately
            if (selectedNotification) {
                setSelectedNotification({
                    ...selectedNotification,
                    isRead: true,
                    readAt: new Date().toISOString(),
                });
            }
        },
    });

    const [markAsUnread] = useMutation(MARK_NOTIFICATION_AS_UNREAD, {
        onCompleted: () => {
            refetch();
            // Update the selected notification's read status immediately
            if (selectedNotification) {
                setSelectedNotification({
                    ...selectedNotification,
                    isRead: false,
                    readAt: undefined,
                });
            }
        },
    });

    const [markAllAsRead] = useMutation(MARK_ALL_NOTIFICATIONS_AS_READ, {
        onCompleted: () => {
            refetch();
            // If viewing an unread notification, update it
            if (selectedNotification && !selectedNotification.isRead) {
                setSelectedNotification({
                    ...selectedNotification,
                    isRead: true,
                    readAt: new Date().toISOString(),
                });
            }
        },
    });

    useEffect(() => {
        if (
            notificationId &&
            notificationRefs.current[notificationId] &&
            data
        ) {
            setTimeout(() => {
                notificationRefs.current[notificationId]?.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                });
                // Auto-select the notification if it's in the URL
                const notification = data.notifications?.find(
                    (n: Notification) => n.id === notificationId,
                );
                if (notification) {
                    setSelectedNotification(notification);
                    if (!notification.isRead) {
                        markAsRead({ variables: { notificationId } });
                    }
                }
            }, 100);
        }
    }, [notificationId, data, markAsRead]);

    const [deleteNotification] = useMutation(DELETE_NOTIFICATION, {
        onCompleted: () => {
            refetch();
            setSelectedNotification(null);
        },
    });

    const notifications = data?.notifications || [];
    const notificationCount = data?.notificationCount;

    const handleSelectNotification = (notification: Notification) => {
        setSelectedNotification(notification);
        if (!notification.isRead) {
            markAsRead({ variables: { notificationId: notification.id } });
        }
    };

    const handleDelete = (notificationId: string) => {
        setDeleteNotificationId(notificationId);
        setConfirmModalOpen(true);
    };

    const confirmDelete = () => {
        if (deleteNotificationId) {
            deleteNotification({
                variables: { notificationId: deleteNotificationId },
            });
            setDeleteNotificationId(null);
        }
        setConfirmModalOpen(false);
    };

    const handleFilterChange = (newFilter: "all" | "unread") => {
        setFilter(newFilter);
        setOffset(0);
        setHasMore(true);
        // Clear selection if current notification doesn't match new filter
        if (selectedNotification) {
            if (newFilter === "unread" && selectedNotification.isRead) {
                setSelectedNotification(null);
            }
        }
    };

    const loadMore = () => {
        if (!loading && hasMore) {
            setOffset(offset + limit);
        }
    };

    const loadPrevious = () => {
        if (offset > 0) {
            setOffset(Math.max(0, offset - limit));
        }
    };

    const getNotificationIcon = (type: string) => {
        const iconClass = "h-5 w-5";
        switch (type) {
            case "stream_team_invitation":
                return (
                    <EnvelopeIcon className={`${iconClass} text-purple-400`} />
                );
            case "stream_team_acceptance":
                return <CheckIcon className={`${iconClass} text-green-400`} />;
            case "stream_team_request":
                return (
                    <UserGroupIcon className={`${iconClass} text-blue-400`} />
                );
            case "stream_team_application_status":
                return <BellIcon className={`${iconClass} text-yellow-400`} />;
            case "admin_message":
                return (
                    <MegaphoneIcon className={`${iconClass} text-orange-400`} />
                );
            case "shield_tag_earned":
                return <CheckIcon className={`${iconClass} text-amber-400`} />;
            case "system_announcement":
                return (
                    <MegaphoneIcon className={`${iconClass} text-red-400`} />
                );
            default:
                return <BellIcon className={`${iconClass} text-slate-400`} />;
        }
    };

    return (
        <div className="bg-brand_charcoal/90 min-h-screen w-full p-6">
            <div className="mb-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="font-prviking text-brand text-3xl">
                            {convertLetters("Inbox")}
                        </h1>
                        <p className="font-garamond mt-2 text-base text-slate-400">
                            {notificationCount?.total || 0} total notifications,{" "}
                            {notificationCount?.unread || 0} unread
                        </p>
                    </div>
                    <PushNotificationButton variant="village" />
                </div>
            </div>

            <div className="mb-6 flex items-center justify-between gap-4">
                <div className="flex gap-4">
                    <button
                        onClick={() => handleFilterChange("all")}
                        className={`font-prviking rounded-lg px-6 py-2 transition-all ${
                            filter === "all"
                                ? "bg-brand text-slate-900"
                                : "border-2 border-slate-700 bg-slate-900/50 text-slate-400 hover:border-slate-600 hover:text-slate-300"
                        }`}
                    >
                        {convertLetters("All")}
                    </button>
                    <button
                        onClick={() => handleFilterChange("unread")}
                        className={`font-prviking rounded-lg px-6 py-2 transition-all ${
                            filter === "unread"
                                ? "bg-brand text-slate-900"
                                : "border-2 border-slate-700 bg-slate-900/50 text-slate-400 hover:border-slate-600 hover:text-slate-300"
                        }`}
                    >
                        {convertLetters("Unread")} (
                        {notificationCount?.unread || 0})
                    </button>
                </div>
                {notificationCount && notificationCount.unread > 0 && (
                    <button
                        onClick={() => markAllAsRead()}
                        className="font-prviking border-brand bg-brand/10 text-brand hover:bg-brand rounded-lg border-2 px-4 py-2 transition-all hover:text-slate-900"
                    >
                        {convertLetters("Mark All as Read")}
                    </button>
                )}
            </div>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                {/* Notifications List */}
                <div className="overflow-hidden rounded-lg border-2 border-slate-700 bg-slate-900/50 backdrop-blur-sm lg:col-span-1">
                    <div className="divide-y divide-slate-700">
                        {loading && (
                            <div className="font-garamond p-8 text-center text-slate-400">
                                <div className="border-brand mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-4 border-solid border-r-transparent" />
                                <p>{convertLetters("Loading...")}</p>
                            </div>
                        )}

                        {!loading && notifications.length === 0 && (
                            <div className="font-garamond p-8 text-center text-slate-400">
                                <BellIcon className="mx-auto mb-4 h-12 w-12" />
                                <p>
                                    {convertLetters("No notifications found")}
                                </p>
                            </div>
                        )}

                        {!loading &&
                            notifications.map((notification: Notification) => (
                                <button
                                    key={notification.id}
                                    ref={(el) => {
                                        notificationRefs.current[
                                            notification.id
                                        ] = el;
                                    }}
                                    onClick={() =>
                                        handleSelectNotification(notification)
                                    }
                                    className={`font-garamond w-full p-4 text-left transition-all duration-300 ${
                                        selectedNotification?.id ===
                                        notification.id
                                            ? "bg-slate-800/70"
                                            : "hover:bg-slate-800/50"
                                    } ${
                                        !notification.isRead
                                            ? "border-brand border-l-4 bg-slate-800/40"
                                            : ""
                                    } ${
                                        notificationId === notification.id
                                            ? "ring-brand ring-2 ring-offset-2 ring-offset-slate-900"
                                            : ""
                                    }`}
                                >
                                    <div className="flex gap-3">
                                        <div className="mt-1 shrink-0">
                                            {getNotificationIcon(
                                                notification.notificationType,
                                            )}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-start justify-between gap-2">
                                                <p
                                                    className={`font-garamond text-sm ${
                                                        notification.isRead
                                                            ? "text-slate-400"
                                                            : "font-semibold text-white"
                                                    }`}
                                                >
                                                    {notification.title}
                                                </p>
                                                {!notification.isRead && (
                                                    <span className="bg-brand h-2 w-2 shrink-0 rounded-full"></span>
                                                )}
                                            </div>
                                            <p className="font-garamond mt-1 text-xs text-slate-600">
                                                {formatDistanceToNow(
                                                    new Date(
                                                        notification.createdAt,
                                                    ),
                                                    { addSuffix: true },
                                                )}
                                            </p>
                                        </div>
                                    </div>
                                </button>
                            ))}
                    </div>
                    {/* Pagination Controls */}
                    {(offset > 0 || hasMore) && (
                        <div className="border-t-2 border-slate-700 p-4">
                            <div className="flex justify-between">
                                <button
                                    onClick={loadPrevious}
                                    disabled={offset === 0}
                                    className="font-prviking rounded-lg px-4 py-2 text-sm enabled:border-2 enabled:border-slate-700 enabled:bg-slate-900/50 enabled:text-slate-400 enabled:hover:border-slate-600 enabled:hover:text-slate-300 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    {convertLetters("Previous")}
                                </button>
                                <button
                                    onClick={loadMore}
                                    disabled={!hasMore || loading}
                                    className="font-prviking rounded-lg px-4 py-2 text-sm enabled:border-2 enabled:border-slate-700 enabled:bg-slate-900/50 enabled:text-slate-400 enabled:hover:border-slate-600 enabled:hover:text-slate-300 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    {convertLetters("Next")}
                                </button>
                            </div>
                        </div>
                    )}
                </div>{" "}
                {/* Notification Detail */}
                <div className="rounded-lg border-2 border-slate-700 bg-slate-900/50 p-6 backdrop-blur-sm lg:col-span-2">
                    {!selectedNotification && (
                        <div className="font-garamond py-12 text-center text-slate-500">
                            <BellIcon className="mx-auto mb-4 h-16 w-16" />
                            <p>
                                {convertLetters(
                                    "Select a notification to view details",
                                )}
                            </p>
                        </div>
                    )}

                    {selectedNotification && (
                        <div>
                            <div className="mb-6 flex items-start justify-between">
                                <div className="flex gap-3">
                                    <div className="text-4xl">
                                        {getNotificationIcon(
                                            selectedNotification.notificationType,
                                        )}
                                    </div>
                                    <div>
                                        <h2 className="norse-font text-2xl font-bold text-amber-100">
                                            {selectedNotification.title}
                                        </h2>
                                        <p className="mt-1 text-sm text-amber-200/70">
                                            {formatDistanceToNow(
                                                new Date(
                                                    selectedNotification.createdAt,
                                                ),
                                                { addSuffix: true },
                                            )}
                                            {selectedNotification.sender && (
                                                <span>
                                                    {" "}
                                                    • From{" "}
                                                    {
                                                        selectedNotification
                                                            .sender
                                                            .twitchUsername
                                                    }
                                                </span>
                                            )}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => {
                                            if (selectedNotification.isRead) {
                                                markAsUnread({
                                                    variables: {
                                                        notificationId:
                                                            selectedNotification.id,
                                                    },
                                                });
                                            } else {
                                                markAsRead({
                                                    variables: {
                                                        notificationId:
                                                            selectedNotification.id,
                                                    },
                                                });
                                            }
                                        }}
                                        className="rounded p-1 transition-colors hover:bg-slate-700/50"
                                        title={
                                            selectedNotification.isRead
                                                ? "Mark as unread"
                                                : "Mark as read"
                                        }
                                    >
                                        {selectedNotification.isRead ? (
                                            <EnvelopeOpenIcon className="h-6 w-6 text-slate-400" />
                                        ) : (
                                            <EnvelopeIcon className="text-brand h-6 w-6" />
                                        )}
                                    </button>
                                    <button
                                        onClick={() =>
                                            handleDelete(
                                                selectedNotification.id,
                                            )
                                        }
                                        className="rounded p-1 transition-colors hover:bg-red-900/50"
                                        title="Delete"
                                    >
                                        <TrashIcon className="h-6 w-6 text-red-400" />
                                    </button>
                                </div>
                            </div>

                            {selectedNotification.imageUrl && (
                                <div className="mb-6">
                                    <img
                                        src={selectedNotification.imageUrl}
                                        alt="Notification"
                                        className="max-w-full rounded-lg border border-amber-900/30"
                                        loading="lazy"
                                    />
                                </div>
                            )}

                            <DiscordMarkdown className="prose prose-invert prose-amber max-w-none text-amber-100">
                                {selectedNotification.message}
                            </DiscordMarkdown>

                            {selectedNotification.actionUrl && (
                                <div className="mt-6 border-t border-amber-900/30 pt-6">
                                    <Link
                                        to={selectedNotification.actionUrl}
                                        className="inline-flex items-center gap-2 rounded bg-amber-600 px-6 py-3 font-medium text-amber-100 transition-colors hover:bg-amber-700"
                                    >
                                        View Details
                                    </Link>
                                </div>
                            )}

                            {(selectedNotification.isBroadcast ||
                                selectedNotification.isGroupMessage) && (
                                <div className="mt-4 flex gap-2">
                                    {selectedNotification.isBroadcast && (
                                        <span className="inline-flex items-center rounded border border-purple-700/50 bg-purple-900/50 px-3 py-1 text-xs font-medium text-purple-200">
                                            Broadcast
                                        </span>
                                    )}
                                    {selectedNotification.isGroupMessage && (
                                        <span className="inline-flex items-center rounded border border-blue-700/50 bg-blue-900/50 px-3 py-1 text-xs font-medium text-blue-200">
                                            Group Message
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Confirmation Modal */}
            <VillageModal
                isOpen={confirmModalOpen}
                onClose={() => {
                    setConfirmModalOpen(false);
                    setDeleteNotificationId(null);
                }}
                onConfirm={confirmDelete}
                title="Delete Notification"
                message="Are you sure you want to delete this notification?"
                type="confirm"
            />
        </div>
    );
}
